import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { UserCheck, Plus, Loader2, Tags, CalendarDays, Search, Copy, UserX, X } from "lucide-react";
import { QuickAssignDialog } from "../components/QuickAssignDialog";
import { CopyAssignmentsDialog } from "../components/CopyAssignmentsDialog";
import { AssignmentsTable } from "../components/AssignmentsTable";
import { ActivityTypesEditor } from "../components/ActivityTypesEditor";
import { fmtH } from "../lib/planning";

const selectCls = "h-9 rounded-md border border-slate-200 bg-white px-2 text-sm";

export default function AssegnazioniPage() {
    const [rows, setRows] = useState(null);
    const [q, setQ] = useState("");
    const [wsFilter, setWsFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [showNew, setShowNew] = useState(false);
    const [showCopy, setShowCopy] = useState(false);
    const [showTypes, setShowTypes] = useState(false);
    const [batches, setBatches] = useState([]);

    const load = useCallback(async () => {
        try {
            const [{ data }, b] = await Promise.all([
                api.get("/presenze/assignments"),
                api.get("/presenze/substitutions/batches").catch(() => ({ data: [] })),
            ]);
            setRows(data);
            setBatches(b.data || []);
        } catch { toast.error("Errore nel caricamento delle assegnazioni"); setRows([]); }
    }, []);

    const cancelBatch = async (b) => {
        if (!window.confirm(`Annullare la sostituzione di ${b.absent_name} da parte di ${b.substitute_name} (${b.count} turni)?`)) return;
        try {
            await api.delete(`/presenze/substitutions/batch/${b.batch_id}`);
            toast.success("Sostituzione annullata");
            load();
        } catch { toast.error("Errore nell'annullamento"); }
    };

    useEffect(() => { load(); }, [load]);

    const worksites = useMemo(() => {
        const m = new Map();
        (rows || []).forEach((r) => m.set(r.worksite_id, r.worksite_name + (r.client_name ? ` — ${r.client_name}` : "")));
        return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
    }, [rows]);
    const types = useMemo(() => [...new Set((rows || []).map((r) => r.activity_name))].sort(), [rows]);

    const visible = useMemo(() => {
        const s = q.trim().toLowerCase();
        return (rows || []).filter((r) =>
            (!wsFilter || r.worksite_id === wsFilter) &&
            (!typeFilter || r.activity_name === typeFilter) &&
            (!s || [r.employee_name, r.matricola, r.worksite_name, r.client_name, r.activity_name].some((v) => String(v || "").toLowerCase().includes(s))));
    }, [rows, q, wsFilter, typeFilter]);

    const remove = async (r) => {
        if (!window.confirm(`Rimuovere l'assegnazione di ${r.employee_name} a ${r.worksite_name} (${r.activity_name})?`)) return;
        try {
            await api.delete(`/presenze/assignments/${r.id}`);
            toast.success("Assegnazione rimossa");
            load();
        } catch { toast.error("Errore nella rimozione"); }
    };

    const employeesCount = new Set(visible.map((r) => r.employee_id)).size;
    const totalHours = visible.reduce((s, r) => s + (r.hours_week || 0), 0);

    return (
        <div className="mx-auto max-w-7xl px-6 py-8" data-testid="assegnazioni-page">
            <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                        <UserCheck className="h-6 w-6" />
                    </div>
                    <div>
                        <h1 className="font-display text-2xl font-bold text-slate-800">Assegnazioni</h1>
                        <p className="text-sm text-slate-500">
                            Chi lavora dove: dipendente ↔ cliente/cantiere con attività, giorni, orario e frequenza.
                            {rows && <> {visible.length} assegnazioni · {employeesCount} dipendenti · <span className="font-mono-numbers">{fmtH(totalHours)}</span> h/sett.</>}
                        </p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Link to="/presenze/pianificazione">
                        <Button variant="outline" className="gap-2" data-testid="assegnazioni-pianificazione-btn"><CalendarDays className="h-4 w-4" /> Pianificazione</Button>
                    </Link>
                    <Button variant="outline" className="gap-2" onClick={() => setShowTypes(true)} data-testid="assegnazioni-types-btn"><Tags className="h-4 w-4" /> Tipologie attività</Button>
                    <Button variant="outline" className="gap-2" onClick={() => setShowCopy(true)} data-testid="assegnazioni-copy-btn"><Copy className="h-4 w-4" /> Copia assegnazioni</Button>
                    <Button onClick={() => setShowNew(true)} className="gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]" data-testid="assegnazioni-new-btn"><Plus className="h-4 w-4" /> Nuova assegnazione</Button>
                </div>
            </div>

            <div className="mb-4 flex flex-wrap items-center gap-2">
                <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca dipendente, cantiere, cliente, attività…" className="h-9 w-72 pl-8" data-testid="assegnazioni-search" />
                </div>
                <select value={wsFilter} onChange={(e) => setWsFilter(e.target.value)} className={selectCls} data-testid="assegnazioni-ws-filter">
                    <option value="">Tutti i cantieri</option>
                    {worksites.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                </select>
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={selectCls} data-testid="assegnazioni-type-filter">
                    <option value="">Tutte le attività</option>
                    {types.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
            </div>

            {batches.length > 0 && (
                <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50/50 p-4" data-testid="substitution-batches">
                    <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-sky-800">
                        <UserX className="h-4 w-4" /> Sostituzioni temporanee in corso o programmate ({batches.length})
                    </div>
                    <ul className="divide-y divide-sky-100">
                        {batches.map((b) => (
                            <li key={b.batch_id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm" data-testid={`batch-row-${b.batch_id}`}>
                                <div className="flex flex-wrap items-center gap-x-2 text-slate-700">
                                    <b>{b.substitute_name}</b> sostituisce <b>{b.absent_name}</b>
                                    <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700">{b.reason_label}</span>
                                    <span className="font-mono-numbers text-xs text-slate-500">{new Date(b.date_from).toLocaleDateString("it-IT")} → {new Date(b.date_to).toLocaleDateString("it-IT")}</span>
                                    <span className="text-xs text-slate-500">· {b.count} turni · {b.worksites.join(", ")}</span>
                                    {b.note && <span className="text-xs italic text-slate-500">«{b.note}»</span>}
                                </div>
                                <button type="button" onClick={() => cancelBatch(b)} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-red-600" data-testid={`batch-cancel-${b.batch_id}`}>
                                    <X className="h-3.5 w-3.5" /> Annulla
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                {!rows ? (
                    <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
                ) : (
                    <AssignmentsTable rows={visible} onDelete={remove} testPrefix="asg"
                        emptyText={rows.length ? "Nessuna assegnazione corrisponde ai filtri." : "Nessuna assegnazione ancora: crea la prima con «Nuova assegnazione»."} />
                )}
            </div>

            <QuickAssignDialog open={showNew} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />
            <CopyAssignmentsDialog open={showCopy} onClose={() => setShowCopy(false)} onSaved={() => { setShowCopy(false); load(); }} />
            <Dialog open={showTypes} onOpenChange={(o) => !o && setShowTypes(false)}>
                <DialogContent className="max-w-lg" data-testid="activity-types-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-slate-800">Tipologie attività</DialogTitle>
                        <DialogDescription>Elenco proposto nell'assegnazione rapida (modificabile anche da Impostazioni).</DialogDescription>
                    </DialogHeader>
                    <ActivityTypesEditor />
                </DialogContent>
            </Dialog>
        </div>
    );
}
